import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiService } from '../services/api';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchSelection, setBranchSelection] = useState({
    required: false,
    branches: [],
    tempUser: null
  });
  const [impersonation, setImpersonation] = useState({
    isImpersonating: false,
    originalUser: null,
    impersonatedUser: null
  });

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await apiService.getUser();
          
          // Check if user data indicates impersonation
          if (userData.is_impersonating && userData.real_admin) {
            setImpersonation({
              isImpersonating: true,
              originalUser: userData.real_admin,
              impersonatedUser: {
                id: userData.id,
                username: userData.username,
                email: userData.email,
                branch: userData.branch,
                vehicle_number: userData.vehicle_number
              }
            });
            
            // Set the impersonated user as the current user
            setUser(userData);
          } else {
            // Reset impersonation state for normal users
            setImpersonation({
              isImpersonating: false,
              originalUser: null,
              impersonatedUser: null
            });
            setUser(userData);
          }
        } catch (error) {
          console.error('Failed to load user', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    loadUser();

    // Listen for localStorage changes across tabs (this fixes the multi-tab token issue!)
    const handleStorageChange = (event) => {
      if (event.key === 'token') {
        if (event.newValue === null) {
          // Token was removed in another tab - logout this tab too
          console.log('Token removed in another tab, logging out');
          setUser(null);
          setImpersonation({
            isImpersonating: false,
            originalUser: null,
            impersonatedUser: null
          });
        } else if (event.newValue !== event.oldValue) {
          // Token was updated in another tab - reload user data
          console.log('Token updated in another tab, reloading user');
          loadUser();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = async (credentials) => {
    const response = await apiService.login(credentials);
    
    // Check if branch selection is required
    if (response.requiresBranchSelection) {
      setBranchSelection({
        required: true,
        branches: response.branches,
        tempUser: response.tempUser
      });
      return response;
    }
    
    // Normal login flow
    setUser(response.user);
    localStorage.setItem('token', response.token);
    return response;
  };

  const setPreferredBranch = async (branchName) => {
    try {
      const response = await apiService.setPreferredBranch(branchSelection.tempUser.id, branchName);
      
      // Update user state and token
      setUser(response.user);
      localStorage.setItem('token', response.token);
      
      // Clear branch selection state
      setBranchSelection({
        required: false,
        branches: [],
        tempUser: null
      });
      
      return response;
    } catch (error) {
      console.error('Failed to set preferred branch:', error);
      throw error;
    }
  };

  const startImpersonation = async (targetUserId) => {
    try {
      const response = await apiService.startImpersonation(targetUserId);
      
      // Update impersonation state
      setImpersonation({
        isImpersonating: true,
        originalUser: response.originalUser,
        impersonatedUser: response.impersonatingUser
      });
      
      // Set the impersonated user as current user
      setUser({
        ...response.impersonatingUser,
        is_impersonating: true,
        real_admin: response.originalUser
      });
      
      return response;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await apiService.stopImpersonation();
      
      // Reset impersonation state
      setImpersonation({
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null
      });
      
      // Restore original user
      setUser(response.user);
      
      return response;
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setImpersonation({
      isImpersonating: false,
      originalUser: null,
      impersonatedUser: null
    });
    setBranchSelection({
      required: false,
      branches: [],
      tempUser: null
    });
  };

  const value = {
    user,
    setUser,
    login,
    logout,
    loading,
    branchSelection,
    setPreferredBranch,
    impersonation,
    startImpersonation,
    stopImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};